// Phase 3 C4 — admin panel for editing the client + case folder templates.
// Mirrors the bankAccounts pattern in SettingsView: lock/unlock toggle, add
// row, inline edit, delete. Persists via PUT /api/firm-settings.
//
// Forward-only semantics — note rendered below the panels per the brief.

import React, { useState, useEffect, lazy, Suspense } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import { Lock, Unlock, Trash2, Plus, Folder } from "lucide-react";
/* 211 §12–§14 — the propagation window loads on use (its second purpose, the
   subfolder check, made it worth its own chunk; the clients list opens it too). */
const FolderPropagationModal = lazy(() => import("./FolderPropagationModal"));
import { RAD, FS } from "../lib/tokens";
import MenuSelect from "./MenuSelect"; // 154B §2
import type { Translate } from "../lib/interestRefusal"; // 211 §14

function toast(msg: any) {
  // Lazy import — same global toast used elsewhere. Falls back to alert in
  // dev contexts without the helper.
  if (typeof window !== "undefined" && window.lxToast) window.lxToast(msg);
}

// Phase 36 (B) — plain-language "what is filed here" labels for the protected
// basis subfolders, keyed by stable role (NOT the editable display name). Kept
// in sync with server/src/lib/folderTemplates.js (CLIENT_BASIS / CASE_BASIS).
const ROLE_FUNCTION_FALLBACK: Record<string, string> = {
  mission: "Engagement & matter correspondence are filed here",
  duedil: "Client due-diligence (CDD) documents are filed here",
  confid: "Confidential / restricted documents are filed here",
  factu: "Invoices are saved here",
};

// Phase 36 (B) — normalise an entry to the {role?, name} object shape so the
// panel tolerates a legacy string array arriving before the server has
// re-saved it in the new shape.
function asEntry(el: any) {
  if (typeof el === "string") return { name: el };
  if (el && typeof el === "object")
    return { id: el.id, role: el.role, basis: el.basis === true || !!el.role, name: el.name ?? "" };
  return { name: "" };
}

type TemplateListProps = {
  title?: any;
  gateKey?: any;
  listKey?: any;
  value?: any;
  onSave?: any;
  readOnly?: boolean;
};
// Stage-3 follow-up — `readOnly` freezes a whole list's NAMES this phase: the
// name input is disabled and the add/delete/lock controls are hidden. Used for
// the case template, whose subfolder names are bound by name-based consumers
// (doc generation, interest-calc) until the settings phase rebinds them by id —
// renaming now would silently break those. NOTE: this is a per-LIST freeze, NOT
// keyed off the basis flag (client basis names stay editable).
function TemplateList({ title, gateKey, listKey, value, onSave, readOnly = false }: TemplateListProps) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canEdit = can(gateKey) && !readOnly;
  const [items, setItems] = useState<any[]>(() => (Array.isArray(value) ? value.map(asEntry) : []));
  const [locked, setLocked] = useState(true);
  const [adding, setAdding] = useState("");

  useEffect(() => {
    setItems(Array.isArray(value) ? value.map(asEntry) : []);
  }, [value]);

  const commit = async (next: any) => {
    setItems(next);
    try {
      await onSave(listKey, next);
    } catch (e: any) {
      toast("⚠ " + (e.message || t("common.save_failed", { fallback: "Save failed" })));
    }
  };

  return (
    <div
      style={{
        marginBottom: 14,
        padding: 12,
        border: "1px solid var(--br)",
        borderRadius: RAD.r8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <h4 style={{ margin: 0, fontSize: FS.s13, fontWeight: 600, flex: 1 }}>
          {title}
        </h4>
        {canEdit && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setLocked((v) => !v)}
            style={{ padding: "2px 8px", fontSize: FS.s10 }}
            title={
              locked
                ? t("ui.editing", { fallback: "Edit" })
                : t("ui.locked", { fallback: "Lock" })
            }
          >
            {locked ? <Lock size={11} /> : <Unlock size={11} />}{" "}
            {locked
              ? t("ui.locked", { fallback: "Locked" })
              : t("ui.editing", { fallback: "Editing" })}
          </button>
        )}
      </div>
      {items.length === 0 && (
        <p style={{ fontSize: FS.s11, color: "var(--tm)", margin: "4px 0" }}>—</p>
      )}
      {items.map((entry, i) => {
        // Stage-3 correction — basis = locked structural folder (client OR case).
        // Caption only for CLIENT basis (which carries a role/function); case
        // basis is locked but caption-less (no feature files by role yet).
        const isBasis = entry.basis === true || !!entry.role;
        const fnLabel = entry.role
          ? t("settings.folder_roles." + entry.role, {
              fallback: ROLE_FUNCTION_FALLBACK[entry.role] || "Structural folder",
            })
          : null;
        return (
          <div key={i} style={{ marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {/* Phase 36 (B) — basis rows carry a lock so the admin can see
                  they are structural (can be renamed/reordered, not removed). */}
              {isBasis && (
                <Lock
                  size={11}
                  style={{ color: "var(--ac)", flexShrink: 0 }}
                  {...({
                    title: t("settings.folder_roles.protected", {
                      fallback: "Structural folder — cannot be removed",
                    }),
                  } as any)}
                />
              )}
              <input
                className="field-input"
                style={{ flex: 1, margin: 0, fontSize: FS.s11 }}
                value={entry.name}
                disabled={locked || !canEdit}
                onChange={(e) => {
                  const next = [...items];
                  next[i] = { ...next[i], name: e.target.value };
                  setItems(next);
                }}
                onBlur={(e) => {
                  const prev = Array.isArray(value) ? asEntry(value[i]) : null;
                  if (!locked && canEdit && e.target.value !== (prev?.name ?? "")) {
                    commit(items);
                  }
                }}
              />
              {/* Only extras (role-less) are deletable; basis is protected. */}
              {!locked && canEdit && !isBasis && (
                <button
                  type="button"
                  className="btn"
                  style={{
                    padding: "2px 6px",
                    fontSize: FS.s10,
                    background: "rgba(248,113,113,.1)",
                    color: "var(--rd)",
                    border: "1px solid rgba(248,113,113,.3)",
                  }}
                  onClick={() => commit(items.filter((_, j) => j !== i))}
                  title={t("common.delete", { fallback: "Delete" })}
                >
                  <Trash2 size={10} />
                </button>
              )}
            </div>
            {fnLabel && (
              <p
                style={{
                  fontSize: FS.s9_5,
                  color: "var(--tm)",
                  margin: "1px 0 0",
                  paddingLeft: 17,
                  fontStyle: "italic",
                }}
              >
                {fnLabel}
              </p>
            )}
          </div>
        );
      })}
      {!locked && canEdit && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 6,
          }}
        >
          <input autoComplete="off" /* 207 §18 */
            className="field-input no-contact-autofill"
            style={{ flex: 1, margin: 0, fontSize: FS.s11 }}
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            placeholder={t("common.add", { fallback: "+ Add subfolder name" })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && adding.trim()) {
                commit([...items, { name: adding.trim() }]);
                setAdding("");
              }
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            style={{ padding: "2px 8px", fontSize: FS.s10 }}
            disabled={!adding.trim()}
            onClick={() => {
              if (adding.trim()) {
                commit([...items, { name: adding.trim() }]);
                setAdding("");
              }
            }}
          >
            <Plus size={10} />
          </button>
        </div>
      )}
    </div>
  );
}

// Phase 13 — two firm-level folder settings co-located with the subfolder
// templates: the email-PDF save destination subfolder, and the case folder
// NAME template ({title}/{ref} placeholders).
function FolderExtraSettings({ firmSettings, save }: { firmSettings?: any; save?: any }) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canEdit = can("lateral.caseFolderTemplate.edit");
  // Stage-3 correction — case template entries are {id?, name} objects. The
  // destination settings store the slot's stable ID (rename-safe) when it has
  // one, falling back to the display name for un-minted/legacy slots; the
  // server resolver maps either to the folder's current display name.
  const caseEntries = Array.isArray(firmSettings?.caseFolderTemplate)
    ? firmSettings.caseFolderTemplate
        .map((s: any) => (typeof s === "string" ? { name: s } : s))
        .filter((s: any) => s && typeof s.name === "string" && s.name.trim())
        .map((s: any) => ({ value: s.id || s.name, label: s.name }))
    : [];
  const dest = firmSettings?.emailArchiveSubfolder || "";
  const libDest = firmSettings?.caseLibraryCopySubfolder || "";
  const [tmpl, setTmpl] = useState(
    firmSettings?.caseFolderNameTemplate ?? "{title} - {ref}",
  );
  useEffect(() => {
    setTmpl(firmSettings?.caseFolderNameTemplate ?? "{title} - {ref}");
  }, [firmSettings?.caseFolderNameTemplate]);

  const opts = [...caseEntries];
  if (dest && !opts.some((o) => o.value === dest)) opts.unshift({ value: dest, label: dest });
  // Phase 18 — independent options list for the library-copy select.
  const libOpts = [...caseEntries];
  if (libDest && !libOpts.some((o) => o.value === libDest)) libOpts.unshift({ value: libDest, label: libDest });

  const saveField = async (key: any, value: any) => {
    try {
      await save(key, value);
    } catch (e: any) {
      toast("⚠ " + (e.message || t("common.save_failed", { fallback: "Save failed" })));
    }
  };

  const preview = String(tmpl || "")
    .replace(/\{title\}/g, "Smith v. Jones")
    .replace(/\{ref\}/g, "2026/042")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[\s\-]+|[\s\-]+$/g, "");

  const box = {
    marginBottom: 14,
    padding: 12,
    border: "1px solid var(--br)",
    borderRadius: RAD.r8,
  };
  return (
    <>
      <div style={box}>
        <h4 style={{ margin: "0 0 4px", fontSize: FS.s13, fontWeight: 600 }}>
          {t("settings.email_archive.title", {
            fallback: "Email-PDF save destination",
          })}
        </h4>
        <p
          style={{
            fontSize: FS.s10_5,
            color: "var(--tm)",
            margin: "0 0 8px",
            lineHeight: 1.5,
          }}
        >
          {t("settings.email_archive.help", {
            fallback:
              "Subfolder of each case folder where saved email PDFs are written. Pick one of the case subfolders above.",
          })}
        </p>
        <MenuSelect
          className="field-input"
          style={{ fontSize: FS.s11, maxWidth: 340, margin: 0 }}
          value={dest}
          disabled={!canEdit}
          onChange={(v) => saveField("emailArchiveSubfolder", v || null)}
          options={[
            [
              "",
              t("settings.email_archive.none", {
                fallback: "— None (email save disabled) —",
              }),
            ] as const,
            ...opts.map((o) => [String(o.value), String(o.label)] as const),
          ]}
        />
      </div>
      <div style={box}>
        <h4 style={{ margin: "0 0 4px", fontSize: FS.s13, fontWeight: 600 }}>
          {t("settings.library_copy.title", {
            fallback: "Library PDF copy destination",
          })}
        </h4>
        <p
          style={{
            fontSize: FS.s10_5,
            color: "var(--tm)",
            margin: "0 0 8px",
            lineHeight: 1.5,
          }}
        >
          {t("settings.library_copy.help", {
            fallback:
              "Subfolder of each case folder where copies of 000BIB PDFs are written when attaching a book to a case. Pick one of the case subfolders above.",
          })}
        </p>
        <MenuSelect
          className="field-input"
          style={{ fontSize: FS.s11, maxWidth: 340, margin: 0 }}
          value={libDest}
          disabled={!canEdit}
          onChange={(v) => saveField("caseLibraryCopySubfolder", v || null)}
          options={[
            [
              "",
              t("settings.library_copy.none", {
                fallback: "— None (case-side bib copy disabled) —",
              }),
            ] as const,
            ...libOpts.map((o) => [String(o.value), String(o.label)] as const),
          ]}
        />
      </div>
      <div style={box}>
        <h4 style={{ margin: "0 0 4px", fontSize: FS.s13, fontWeight: 600 }}>
          {t("settings.case_folder_name.title", {
            fallback: "Case folder name template",
          })}
        </h4>
        <p
          style={{
            fontSize: FS.s10_5,
            color: "var(--tm)",
            margin: "0 0 8px",
            lineHeight: 1.5,
          }}
        >
          {t("settings.case_folder_name.help", {
            fallback:
              "Name pattern for newly created case folders. Placeholders: {title}, {ref}.",
          })}
        </p>
        <input
          className="field-input"
          style={{ fontSize: FS.s11, maxWidth: 340, margin: 0 }}
          value={tmpl}
          disabled={!canEdit}
          onChange={(e) => setTmpl(e.target.value)}
          onBlur={(e) => {
            if (
              canEdit &&
              e.target.value !== (firmSettings?.caseFolderNameTemplate ?? "")
            )
              saveField("caseFolderNameTemplate", e.target.value);
          }}
        />
        <p style={{ fontSize: FS.s10, color: "var(--tm)", marginTop: 6 }}>
          {t("settings.case_folder_name.preview", { fallback: "Preview" })}:{" "}
          <code>{preview || "—"}</code>
        </p>
      </div>
    </>
  );
}

export default function FolderTemplatesPanel({ firmSettings, onUpdated }: { firmSettings?: any; onUpdated?: any }) {
  const { t } = useTranslation() as { t: Translate }; // 211 §14 — typed at the seam
  const { can } = usePermissions() as { can: (...a: [key: string]) => boolean };
  // 211 §14 [ADVISOR: the case template's own gate] — the matters' subfolder check
  const canCheckCases = can("lateral.caseFolderTemplate.edit");
  // 212 §14 — the clients' check here too, on the gate the route asks for it (the backfill's, lateral.persons.create)
  const canCheckClients = can("lateral.persons.create");
  // Template-delete-propagation — target of the propagation modal ({kind,name})
  // when a firm-added subfolder row was just removed, else null.
  const [propTarget, setPropTarget] = useState<any>(null);

  const save = async (key: any, value: any) => {
    const res = (await api.put("/firm-settings", { [key]: value })) as {
      removedExtras?: { client?: string[]; case?: string[] };
    } | null;
    if (onUpdated) onUpdated(res);
    // The PUT reports any firm-added (role-less) subfolder row just removed from
    // a template (server diff). Offer to propagate the deletion to existing
    // folders (client takes precedence; case is dormant but handled generally).
    const rx = res && res.removedExtras;
    if (rx) {
      if (Array.isArray(rx.client) && rx.client.length) setPropTarget({ kind: "client", name: rx.client[0] });
      else if (Array.isArray(rx.case) && rx.case.length) setPropTarget({ kind: "case", name: rx.case[0] });
    }
    return res;
  };

  return (
    <div style={{ marginTop: 6 }}>
      {/* Phase 8 F1 — internal h3 removed; the outer sh4 collapsible header in
          SettingsView now provides the heading. */}
      <TemplateList
        title={t("settings.folder_templates.client", {
          fallback: "Client folder subfolders",
        })}
        gateKey="lateral.clientFolderTemplate.edit"
        listKey="clientFolderTemplate"
        value={firmSettings?.clientFolderTemplate}
        onSave={save}
      />
      {/* 212 §14 — THE SAME CHECK IN SETTINGS, beside the client folder
          template, as the matters' sits beside the case template (Laurent:
          « otherwise people will think it's not possible »): the clients
          list's « Vérifier les dossiers », one window, every client. */}
      {canCheckClients && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ margin: "2px 0 10px", padding: "4px 10px", fontSize: FS.s11 }}
          onClick={() => {
            setPropTarget({ kind: "client", purpose: "fill" });
          }}
        >
          <Folder size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />
          {t("folder_check.settings_clients", { fallback: "Check the clients' folders" })}
        </button>
      )}
      {/* Stage-3 follow-up — case subfolder names are FROZEN read-only this
          phase: they're bound by name-based consumers (doc generation,
          interest-calc) that the settings phase will rebind by id; renaming now
          would silently break them. Client list stays editable. */}
      <TemplateList
        title={t("settings.folder_templates.case", {
          fallback: "Case folder subfolders",
        })}
        gateKey="lateral.caseFolderTemplate.edit"
        listKey="caseFolderTemplate"
        value={firmSettings?.caseFolderTemplate}
        onSave={save}
        readOnly={true}
      />
      {/* 211 §14 — beside the removal's propagation, the check it never made:
          which case and collective-case folders lack a subfolder of this
          template, and — after a dry run — the missing ones made. */}
      {canCheckCases && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ margin: "2px 0 10px", padding: "4px 10px", fontSize: FS.s11 }}
          onClick={() => {
            setPropTarget({ kind: "case", purpose: "fill" });
          }}
        >
          <Folder size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />
          {t("folder_fill.check_cases", { fallback: "Check the matters' subfolders" })}
        </button>
      )}
      <FolderExtraSettings firmSettings={firmSettings} save={save} />
      <p
        style={{
          fontSize: FS.s10,
          color: "var(--tm)",
          fontStyle: "italic",
          marginTop: 6,
        }}
      >
        {t("folder_prop.tpl_note", {
          fallback:
            "Removing a firm-added subfolder now offers to propagate the deletion to existing folders — empty copies only. Non-empty folders are skipped for manual review, and basis folders are never removed.",
        })}
      </p>
      {propTarget && (
        <Suspense fallback={null}>
          <FolderPropagationModal
            kind={propTarget.kind}
            name={propTarget.name}
            purpose={propTarget.purpose}
            onClose={() => setPropTarget(null)}
          />
        </Suspense>
      )}
    </div>
  );
}
